import { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Row, Col, Table, Alert } from "react-bootstrap";
import moment from "moment";
import {
  postOrdenPago,
  getCreditosDisponiblesOrdenPago,
} from "../helpers/rutaOrdenesPago.js";
import { getCarteraDisponibles } from "../helpers/rutaCarteras";
import { generarPDFOrdenPago } from "../helpers/pdfOrdenPago";
import { getComandasPendientesTercero } from "../helpers/rutaComandas";
import { generarPDFSaldoCliente } from "../helpers/pdfSaldoCliente";
import { getSaldosTesoreria } from "../helpers/rutaTesoreria";

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const num = (n) => round2(Number(n || 0));
const clamp = (value, min, max) => Math.min(Math.max(num(value), min), max);
const $ = (n) =>
  Number(n || 0).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const buildRowFromPendiente = (c) => ({
  _id: c._id,
  codcom: c.codcom,
  nroviaje: c.nroviaje,
  fecha: c.fecha,
  factura: c.factura || "",
  pactadoBruto: num(c.pactadoBruto ?? c.pactado ?? 0),
  comision: num(c.comision ?? 0),
  merma: num(c.merma ?? 0),
  pactado: num(c.pactado ?? 0),
  pagado: num(c.pagado ?? 0),
  saldo: num(c.saldo ?? ((c.pactado ?? 0) - (c.pagado ?? 0))),
  importeAplicado: num(c.importeAplicado ?? c.saldo ?? ((c.pactado ?? 0) - (c.pagado ?? 0))),
});

export default function ModalOrdenPago({
  show,
  onHide,
  tercero,
  cliente = null,
  comandasPreseleccionadas = [],
}) {
  const [pendientes, setPendientes] = useState([]);
  const [seleccion, setSeleccion] = useState([]);
  const [fecha, setFecha] = useState(moment().format("YYYY-MM-DD"));
  const [observaciones, setObservaciones] = useState("");
  const [filtrarCliente, setFiltrarCliente] = useState(false);

  const [medios, setMedios] = useState([]);
  const [rets, setRets] = useState([]);

  const [cajas, setCajas] = useState([]);
  const [cuentas, setCuentas] = useState([]);

  const [creditosDisponibles, setCreditosDisponibles] = useState([]);
  const [carteraDisponibles, setCarteraDisponibles] = useState([]);
  const [saldosFavorAplicados, setSaldosFavorAplicados] = useState([]);
  const [loadingCreditos, setLoadingCreditos] = useState(false);

  /* ---------------- carga inicial ---------------- */
  useEffect(() => {
    if (!show || !(tercero?._id || tercero)) return;

    (async () => {
      const list = await getComandasPendientesTercero(
        tercero?._id || tercero,
        filtrarCliente ? (cliente?._id || cliente) : undefined
      );

      const ordenadas = (list || [])
        .slice()
        .sort((a, b) => new Date(a.fecha || 0) - new Date(b.fecha || 0));

      const byId = new Map(ordenadas.map((x) => [x._id, x]));

      const merged = Array.from(byId.values()).sort(
        (a, b) => new Date(a.fecha || 0) - new Date(b.fecha || 0)
      );
      setPendientes(merged);

      const mapSel = new Map();

      (comandasPreseleccionadas || []).forEach((c) => {
        const found = byId.get(c._id);

        // Seleccionar únicamente pendientes confirmados por el back.
        if (!found || Number(found.saldo || 0) <= 0.01) return;

        const row = buildRowFromPendiente({
          ...found,
          importeAplicado: found.saldo,
        });

        mapSel.set(row._id, row);
      });

      setSeleccion(Array.from(mapSel.values()));

      try {
        const s = await getSaldosTesoreria();
        if (s?.ok) {
          setCajas(Array.isArray(s.cajas) ? s.cajas : []);
          setCuentas(Array.isArray(s.cuentas) ? s.cuentas : []);
        }
      } catch {
        setCajas([]);
        setCuentas([]);
      }
    })();
  }, [show, tercero?._id, cliente?._id, filtrarCliente, tercero, cliente, comandasPreseleccionadas]);

  useEffect(() => {
    if (!show || !(tercero?._id || tercero)) return;

    (async () => {
      try {
        setLoadingCreditos(true);
        const res = await getCreditosDisponiblesOrdenPago(
          tercero?._id || tercero,
          filtrarCliente ? (cliente?._id || cliente) : undefined
        );
        setCreditosDisponibles(res?.ok ? res.creditos || [] : []);
      } catch {
        setCreditosDisponibles([]);
      } finally {
        setLoadingCreditos(false);
      }
    })();
  }, [show, tercero?._id, tercero, cliente?._id, cliente, filtrarCliente]);

  useEffect(() => {
    if (!show) return;

    (async () => {
      try {
        const res = await getCarteraDisponibles({
          estados: "EN_CARTERA",
        });

        const lista =
          res?.items ||
          res?.cartera ||
          res?.valores ||
          res?.data ||
          [];

        setCarteraDisponibles(Array.isArray(lista) ? lista : []);
      } catch (e) {
        console.error("Error cargando cartera disponible OP:", e);
        setCarteraDisponibles([]);
      }
    })();
  }, [show]);

  /* ---------------- selección de comandas ---------------- */
  const isSelected = (id) => !!seleccion.find((x) => x._id === id);

  const toggleSeleccion = (c) => {
    setSeleccion((prev) => {
      const exists = prev.find((x) => x._id === c._id);
      if (exists) return prev.filter((x) => x._id !== c._id);

      const row = buildRowFromPendiente({
        ...c,
        importeAplicado: c.saldo ?? ((c.pactado ?? 0) - (c.pagado ?? 0)),
      });
      return [...prev, row];
    });
  };

  const updateImporteAplicado = (id, value) => {
    setSeleccion((prev) =>
      prev.map((x) =>
        x._id === id
          ? {
              ...x,
              importeAplicado: clamp(value, 0, num(x.saldo)),
            }
          : x
      )
    );
  };

  const seleccionarTodas = () => {
    const habilitadas = pendientes
      .filter((p) => Number(p.saldo || 0) > 0.01)
      .map((p) =>
        buildRowFromPendiente({
          ...p,
          importeAplicado: p.saldo,
        })
      );
    setSeleccion(habilitadas);
  };

  const deseleccionarTodas = () => setSeleccion([]);

  const aplicarSaldoCompleto = () => {
    setSeleccion((prev) =>
      prev.map((x) => ({
        ...x,
        importeAplicado: num(x.saldo),
      }))
    );
  };

  const limpiarImportesSeleccionados = () => {
    setSeleccion((prev) =>
      prev.map((x) => ({
        ...x,
        importeAplicado: 0,
      }))
    );
  };

  /* ---------------- medios y retenciones ---------------- */
  const addMedio = (m) => setMedios((prev) => [...prev, m]);
  const removeMedio = (i) => setMedios((prev) => prev.filter((_, idx) => idx !== i));
  const addRet = (r) => setRets((prev) => [...prev, r]);
  const removeRet = (i) => setRets((prev) => prev.filter((_, idx) => idx !== i));

  /* ---------------- saldo a favor usado ---------------- */
  const toggleSaldoFavor = (c) => {
    setSaldosFavorAplicados((prev) => {
      const exists = prev.find((x) => x._id === c._id);
      if (exists) return prev.filter((x) => x._id !== c._id);

      return [
        ...prev,
        {
          _id: c._id,
          numero: c.numero,
          fecha: c.fecha,
          disponible: num(c.disponible),
          importe: num(c.disponible),
        },
      ];
    });
  };

  const updateImporteSaldoFavor = (id, value) => {
    setSaldosFavorAplicados((prev) =>
      prev.map((x) =>
        x._id === id
          ? {
              ...x,
              importe: clamp(value, 0, num(x.disponible)),
            }
          : x
      )
    );
  };

  const usarTodoSaldoFavor = () => {
    const rows = (creditosDisponibles || [])
      .filter((x) => num(x.disponible) > 0.01)
      .map((x) => ({
        _id: x._id,
        numero: x.numero,
        fecha: x.fecha,
        disponible: num(x.disponible),
        importe: num(x.disponible),
      }));
    setSaldosFavorAplicados(rows);
  };

  const limpiarSaldosFavor = () => setSaldosFavorAplicados([]);

  /* ---------------- totales ---------------- */
  const aplicacionesPayload = useMemo(
    () =>
      seleccion
        .filter((x) => num(x.importeAplicado) > 0.01)
        .map((x) => ({
          comanda: x._id,
          aplicado: num(x.importeAplicado),
        })),
    [seleccion]
  );

  const totalAplic = useMemo(
    () => round2(aplicacionesPayload.reduce((a, x) => a + num(x.aplicado), 0)),
    [aplicacionesPayload]
  );

  const totalPagos = useMemo(
    () => round2(medios.reduce((a, m) => a + Number(m.importe || 0), 0)),
    [medios]
  );

  const totalRets = useMemo(
    () => round2(rets.reduce((a, r) => a + Number(r.importe || 0), 0)),
    [rets]
  );

  const saldosFavorPayload = useMemo(
    () =>
      (saldosFavorAplicados || [])
        .filter((x) => num(x.importe) > 0.01)
        .map((x) => ({
          ordenPagoOrigen: x._id,
          importe: num(x.importe),
        })),
    [saldosFavorAplicados]
  );

  const totalSaldosFavorAplicados = useMemo(
    () =>
      round2(
        (saldosFavorAplicados || []).reduce((a, x) => a + Number(x.importe || 0), 0)
      ),
    [saldosFavorAplicados]
  );

  const totalDisponible = useMemo(
    () => round2(totalPagos + totalRets + totalSaldosFavorAplicados),
    [totalPagos, totalRets, totalSaldosFavorAplicados]
  );

  const diferencia = useMemo(
    () => round2(totalDisponible - totalAplic),
    [totalDisponible, totalAplic]
  );

  const totalSaldoFavorGenerado = useMemo(
    () => (diferencia > 0.01 ? diferencia : 0),
    [diferencia]
  );

  const hasOrigenFondos = totalDisponible > 0.01;
  const okDistribucion = diferencia >= -0.01;

  /* ---------------- validación de saldos tesorería ---------------- */
  const saldoWarnings = useMemo(() => {
    const warns = [];
    medios.forEach((p, idx) => {
      const imp = Number(p.importe || 0);
      if (imp <= 0) return;

      if (p.medio === "efectivo") {
        const cx = cajas.find((c) => String(c._id) === String(p.caja));
        const saldo = Number(cx?.saldoActual || 0);
        if (p.caja && imp > saldo + 0.0001) {
          warns.push(
            `Pago #${idx + 1}: importe supera saldo de caja (${saldo.toLocaleString("es-AR")}).`
          );
        }
        if (!p.caja) warns.push(`Pago #${idx + 1}: seleccione una Caja para efectivo.`);
      }

      if (p.medio === "transferencia") {
        const ct = cuentas.find((c) => String(c._id) === String(p.cuentaBancaria));
        const saldo = Number(ct?.saldoActual || 0);
        if (p.cuentaBancaria && imp > saldo + 0.0001) {
          warns.push(
            `Pago #${idx + 1}: importe supera saldo de cuenta (${saldo.toLocaleString("es-AR")}).`
          );
        }
        if (!p.cuentaBancaria) {
          warns.push(`Pago #${idx + 1}: seleccione una Cuenta para transferencia.`);
        }
      }
    });
    return warns;
  }, [medios, cajas, cuentas]);

  const canSubmit =
    hasOrigenFondos &&
    okDistribucion &&
    saldoWarnings.length === 0 &&
    (aplicacionesPayload.length > 0 || totalSaldoFavorGenerado > 0.01);

  /* ---------------- aplicar por fecha ---------------- */
  const aplicarPorFechaSegunDisponible = () => {
    let disponible = num(totalDisponible);

    const ordenadas = [...pendientes]
      .filter((p) => num(p.saldo) > 0.01)
      .sort((a, b) => new Date(a.fecha || 0) - new Date(b.fecha || 0));

    const nuevas = [];

    for (const p of ordenadas) {
      if (disponible <= 0.01) break;

      const saldo = num(p.saldo);
      const aplicado = Math.min(saldo, disponible);

      if (aplicado > 0.01) {
        nuevas.push(
          buildRowFromPendiente({
            ...p,
            importeAplicado: aplicado,
          })
        );
        disponible = round2(disponible - aplicado);
      }
    }

    setSeleccion(nuevas);
  };

  const labelValorCartera = (v) => {
    const tipo = v?.tipo || "";
    const nro =
      v?.nro ||
      v?.nroCheque ||
      v?.idICheq ||
      v?.numero ||
      "";

    const banco = v?.bancoEmisor || v?.banco || "";
    const cobradoA = v?.cobradoA || v?.librador || v?.emisor || "";
    const importe = Number(v?.importe || 0).toLocaleString("es-AR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return `${tipo} ${nro} - ${banco} - ${cobradoA} - $ ${importe}`;
  };

  /* ---------------- guardar ---------------- */
  const guardar = async () => {
    if (!hasOrigenFondos) {
      return alert(
        "Debés informar pagos, retenciones o saldo a favor aplicado."
      );
    }

    if (!okDistribucion) {
      return alert(
        "Lo aplicado a comandas supera el total disponible de la orden."
      );
    }

    if (saldoWarnings.length) {
      return alert(saldoWarnings.join("\n"));
    }

    const payload = {
      tercero: tercero?._id || tercero,
      cliente: filtrarCliente ? (cliente?._id || cliente) : undefined,
      fecha,
      observaciones,
      aplicaciones: aplicacionesPayload,
      saldosFavorAplicados: saldosFavorPayload,
      pagos: medios
        .filter((m) => num(m.importe) > 0.01)
        .map((m) => {
          const medio =
            m.medio === "icheq" ? "echeq" : (m.medio || "transferencia");

          const base = {
            medio,
            importe: Number(m.importe || 0),

            referencia: m.referencia || (medio === "echeq" ? "ICHEQ" : ""),
            banco: m.banco || "",
            cbu: m.cbu || "",

            // cheque
            nroCheque: m.nroCheque || "",
            bancoEmisor: m.bancoEmisor || "",
            librador: m.librador || "",
            cuitLibrador: m.cuitLibrador || "",
            fechaCheque: m.fechaCheque || null,
            fechaPagoDiferido: m.fechaPagoDiferido || m.fechaCheque || null,

            // echeq / icheq
            idICheq: m.idICheq || m.nroCheque || "",
            emisor: m.emisor || "",
            cuitEmisor: m.cuitEmisor || "",
            fechaLiquidacion: m.fechaLiquidacion || m.fechaCheque || null,

            carteraItem: m.carteraItem || null,
            observaciones: m.observaciones || "",
          };

          if (medio === "efectivo") return { ...base, caja: m.caja };

          if (medio === "transferencia") {
            return { ...base, cuentaBancaria: m.cuentaBancaria };
          }

          return base;
        }),
      retenciones: rets
        .filter((r) => num(r.importe) > 0.01)
        .map((r) => ({
          tipo: (r.tipo || "IIBB").toUpperCase(),
          base: Number(r.base || 0),
          porcentaje: Number(r.porcentaje || 0),
          importe: Number(r.importe || 0),
          comprobante: r.comprobante || "",
        })),
    };

    console.log("OP payload >>>", JSON.stringify(payload, null, 2));

    const res = await postOrdenPago(payload);
    if (res?.ok) {
      alert("Orden de pago guardada correctamente.");
      try {
        generarPDFOrdenPago(res.orden);
      } catch (e) {
        console.error("PDF OP:", e);
      }
      onHide(true);
    } else {
      console.error("OP error:", res);
      alert(res?.msg || "Error al guardar la orden de pago.");
    }
  };

  /* ---------------- exportar PDF ---------------- */
  const nombreTercero = (
    tercero?.transporte ||
    tercero?.razonsocial ||
    tercero?.nombre ||
    ""
  ).toString();

  const nombreCliente = (
    cliente?.razonsocial ||
    cliente?.nombre ||
    ""
  )?.toString() || "";

  const buildItems = (lista) => {
    return lista.map((c) => {
      const pactado = num(c.pactado || 0);
      const aplicado = num(c.pagado ?? 0);
      const saldo = num(c.saldo || 0);

      return {
        numero: String(c.codcom || ""),
        nroviaje: String(c.nroviaje || ""),
        fecha: c.fecha ? new Date(c.fecha) : null,
        total: pactado,
        pactadoBruto: num(c.pactadoBruto ?? c.pactado ?? 0),
        comision: num(c.comision ?? 0),
        merma: num(c.merma ?? 0),
        aplicado,
        saldo,
      };
    });
  };

  const exportarTodoPDF = () => {
    if (!(pendientes?.length)) return alert("No hay comandas pendientes para exportar.");
    const items = buildItems(
      pendientes.map((c) => ({
        ...c,
        importeAplicado: c.pagado,
      }))
    );
    const totalSaldo = items.reduce((s, i) => s + Number(i.saldo || 0), 0);

    generarPDFSaldoCliente({
      clienteNombre: `${nombreTercero}${filtrarCliente && nombreCliente ? " — Cliente: " + nombreCliente : ""}`,
      fechaCorte: fecha ? new Date(fecha) : new Date(),
      data: {
        totalSaldoComandas: totalSaldo,
        comandasConSaldo: items,
        ordenesCobroAbiertas: [],
      },
      tipoEntidad: "TRANSPORTE",
      titulo: "SALDOS PENDIENTES A TERCEROS",
    });
  };

  const exportarSeleccionadasPDF = () => {
    if (!(seleccion?.length)) return alert("No hay comandas seleccionadas para exportar.");
    const items = buildItems(seleccion);
    const totalSaldo = items.reduce((s, i) => s + Number(i.saldo || 0), 0);

    generarPDFSaldoCliente({
      clienteNombre: `${nombreTercero}${filtrarCliente && nombreCliente ? " — Cliente: " + nombreCliente : ""}`,
      fechaCorte: fecha ? new Date(fecha) : new Date(),
      data: {
        totalSaldoComandas: totalSaldo,
        comandasConSaldo: items,
        ordenesCobroAbiertas: [],
      },
      tipoEntidad: "TRANSPORTE",
      titulo: "SALDOS PENDIENTES A TERCEROS",
    });
  };

  return (
    <Modal show={show} onHide={() => onHide(false)} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Nueva Orden de Pago</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Row className="mb-2">
          <Col md={4}>
            <Form.Label>Tercero</Form.Label>
            <Form.Control readOnly value={nombreTercero} />
          </Col>
          <Col md={4}>
            <Form.Label>Cliente (filtro opcional)</Form.Label>
            <Form.Control readOnly value={nombreCliente} placeholder="(sin filtro)" />
            <div className="mt-1">
              <Form.Check
                type="switch"
                id="filtrar-por-cliente"
                label="Filtrar por este cliente"
                checked={filtrarCliente}
                onChange={(e) => setFiltrarCliente(e.target.checked)}
              />
            </div>
          </Col>
          <Col md={4}>
            <Form.Label>Fecha</Form.Label>
            <Form.Control
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
            />
          </Col>
        </Row>

        <Row className="align-items-center mb-2">
          <Col>
            <b>Comandas pendientes del tercero</b>
          </Col>
          <Col className="text-end">
            <Button size="sm" variant="outline-secondary" onClick={seleccionarTodas}>
              Tildar todas
            </Button>{" "}
            <Button size="sm" variant="outline-secondary" onClick={deseleccionarTodas}>
              Destildar todas
            </Button>{" "}
            <Button size="sm" variant="outline-primary" onClick={aplicarSaldoCompleto}>
              Aplicar saldo completo
            </Button>{" "}
            <Button size="sm" variant="outline-warning" onClick={limpiarImportesSeleccionados}>
              Poner importes en 0
            </Button>{" "}
            <Button size="sm" variant="warning" onClick={aplicarPorFechaSegunDisponible}>
              Aplicar por fecha según disponible
            </Button>{" "}
            <Button
              size="sm"
              variant="success"
              className="ms-1"
              onClick={exportarTodoPDF}
              style={{ minWidth: 150 }}
            >
              Exportar TODO PDF
            </Button>
            <Button
              size="sm"
              variant="outline-success"
              className="ms-1"
              onClick={exportarSeleccionadasPDF}
              disabled={!seleccion.length}
              style={{ minWidth: 190 }}
            >
              Exportar SELECCIONADAS PDF
            </Button>
            {Boolean(seleccion.length) && (
              <span className="ms-2">
                Seleccionadas: <b>{seleccion.length}</b> — Σ: <b>${$(totalAplic)}</b>
              </span>
            )}
          </Col>
        </Row>

        <div
          style={{
            maxHeight: 320,
            overflowY: "auto",
            border: "1px solid #e0e0e0",
            position: "relative",
          }}
        >
          <Table size="sm" bordered hover className="m-0">
            <thead>
              <tr style={{ position: "sticky", top: 0, background: "#f8f9fa", zIndex: 2 }}>
                <th style={{ width: 40, textAlign: "center" }}></th>
                <th>Comanda</th>
                <th>Viaje</th>
                <th>Fecha</th>
                <th>Factura</th>
                <th style={{ textAlign: "right" }}>Pactado neto</th>
                <th style={{ textAlign: "right" }}>Pagado</th>
                <th style={{ textAlign: "right" }}>Saldo</th>
                <th style={{ textAlign: "right", width: 150 }}>Aplicar</th>
              </tr>
            </thead>
            <tbody>
              {pendientes.map((c) => {
                const checked = !!seleccion.find((x) => x._id === c._id);
                const disabled = Number(c.saldo || 0) <= 0.01;
                const rowSel = seleccion.find((x) => x._id === c._id);
                const inputId = `sel-op-${c._id}`;

                return (
                  <tr key={c._id} style={disabled ? { opacity: 0.5 } : undefined}>
                    <td style={{ width: 40, textAlign: "center" }}>
                      <input
                        id={inputId}
                        type="checkbox"
                        className="form-check-input m-0 p-0"
                        style={{ marginLeft: 0, position: "static" }}
                        disabled={disabled}
                        checked={checked}
                        onChange={() => toggleSeleccion(c)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td>
                      <label htmlFor={inputId} className="w-100 m-0">
                        #{String(c.codcom).padStart(6, "0")}
                      </label>
                    </td>
                    <td>
                      <label htmlFor={inputId} className="w-100 m-0">
                        {c.nroviaje}
                      </label>
                    </td>
                    <td>
                      <label htmlFor={inputId} className="w-100 m-0">
                        {c.fecha ? moment(c.fecha).format("DD/MM/YYYY") : ""}
                      </label>
                    </td>
                    <td>
                      <label htmlFor={inputId} className="w-100 m-0">
                        {c.factura || ""}
                      </label>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <label htmlFor={inputId} className="w-100 m-0">
                        ${$(c.pactado ?? 0)}
                      </label>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <label htmlFor={inputId} className="w-100 m-0">
                        ${$(c.pagado ?? 0)}
                      </label>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <label htmlFor={inputId} className="w-100 m-0">
                        ${$(c.saldo ?? 0)}
                      </label>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        disabled={!checked}
                        value={checked ? num(rowSel?.importeAplicado) : ""}
                        onChange={(e) =>
                          updateImporteAplicado(c._id, e.target.value)
                        }
                        onBlur={(e) =>
                          updateImporteAplicado(c._id, e.target.value)
                        }
                      />
                    </td>
                  </tr>
                );
              })}

              {!pendientes.length && (
                <tr>
                  <td colSpan={9} className="text-center text-muted">
                    Sin pendientes para este tercero/cliente
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        <Row className="mt-3">
          <Col md={12}>
            <h6>Saldo a favor disponible</h6>

            <div className="d-flex gap-2 mb-2">
              <Button size="sm" variant="outline-secondary" onClick={usarTodoSaldoFavor}>
                Usar todo
              </Button>
              <Button size="sm" variant="outline-secondary" onClick={limpiarSaldosFavor}>
                Limpiar
              </Button>
            </div>

            {loadingCreditos && (
              <Alert variant="light" className="py-2">
                Cargando saldos a favor disponibles...
              </Alert>
            )}

            {!loadingCreditos && (
              <div
                style={{
                  maxHeight: 180,
                  overflowY: "auto",
                  border: "1px solid #e0e0e0",
                }}
              >
                <Table size="sm" bordered hover className="m-0">
                  <thead>
                    <tr>
                      <th style={{ width: 40, textAlign: "center" }}></th>
                      <th>OP Origen</th>
                      <th>Fecha</th>
                      <th style={{ textAlign: "right" }}>Disponible</th>
                      <th style={{ textAlign: "right", width: 150 }}>Usar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(creditosDisponibles || []).map((c) => {
                      const checked = !!saldosFavorAplicados.find((x) => x._id === c._id);
                      const row = saldosFavorAplicados.find((x) => x._id === c._id);

                      return (
                        <tr key={c._id}>
                          <td style={{ textAlign: "center" }}>
                            <input
                              type="checkbox"
                              className="form-check-input m-0 p-0"
                              checked={checked}
                              onChange={() => toggleSaldoFavor(c)}
                            />
                          </td>
                          <td>{c.numero}</td>
                          <td>{c.fecha ? moment(c.fecha).format("DD/MM/YYYY") : ""}</td>
                          <td style={{ textAlign: "right" }}>${$(c.disponible)}</td>
                          <td style={{ textAlign: "right" }}>
                            <Form.Control
                              size="sm"
                              type="number"
                              step="0.01"
                              disabled={!checked}
                              value={checked ? num(row?.importe) : ""}
                              onChange={(e) =>
                                updateImporteSaldoFavor(c._id, e.target.value)
                              }
                            />
                          </td>
                        </tr>
                      );
                    })}

                    {!creditosDisponibles.length && !loadingCreditos && (
                      <tr>
                        <td colSpan={5} className="text-center text-muted">
                          No hay saldo a favor disponible para este tercero
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </Col>
        </Row>

        <Row className="mt-3">
          <Col md={6}>
            <h6>Pagos</h6>
            <Button
              size="sm"
              onClick={() => addMedio({ medio: "transferencia", importe: 0, cuentaBancaria: "" })}
            >
              + Transferencia
            </Button>{" "}
            <Button
              size="sm"
              onClick={() => addMedio({ medio: "cheque", importe: 0, nroCheque: "", fechaCheque: "" })}
            >
              + Cheque
            </Button>{" "}
            <Button
              size="sm"
              onClick={() =>
                addMedio({
                  medio: "echeq",
                  importe: 0,
                  idICheq: "",
                  bancoEmisor: "",
                  emisor: "",
                  fechaLiquidacion: "",
                  referencia: "ICHEQ",
                })
              }
            >
              + iCheq
            </Button>{" "}
            <Button
              size="sm"
              onClick={() => addMedio({ medio: "efectivo", importe: 0, caja: "" })}
            >
              + Efectivo
            </Button>

            <Table size="sm" className="mt-2">
              <thead>
                <tr>
                  <th style={{ width: 120, minWidth: 120 }}>Medio</th>
                  <th>Origen / Datos</th>
                  <th>Importe</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {medios.map((m, i) => {
                  const medio = m.medio;
                  const selCaja = cajas.find((c) => String(c._id) === String(m.caja));
                  const selCta = cuentas.find((c) => String(c._id) === String(m.cuentaBancaria));

                  return (
                    <tr key={i}>
                      <td style={{ width: 120, minWidth: 120 }}>
                        <Form.Select
                          size="sm"
                          value={m.medio || "transferencia"}
                          onChange={(e) => {
                            const nuevoMedio = e.target.value;
                            setMedios((prev) =>
                              prev.map((x, idx) =>
                                idx === i
                                  ? {
                                      ...x,
                                      medio: nuevoMedio,
                                      carteraItem: null,
                                      importe: 0,
                                      referencia: "",
                                      nroCheque: "",
                                      idICheq: "",
                                      bancoEmisor: "",
                                      emisor: "",
                                      librador: "",
                                    }
                                  : x
                              )
                            );
                          }}
                          style={{ width: "100%", minWidth: 110 }}
                        >
                          <option value="transferencia">Transferencia</option>
                          <option value="cheque">Cheque</option>
                          <option value="echeq">iCheq</option>
                          <option value="efectivo">Efectivo</option>
                        </Form.Select>
                      </td>

                      <td>
                        {medio === "efectivo" && (
                          <div className="d-flex gap-2">
                            <Form.Select
                              size="sm"
                              value={m.caja || ""}
                              style={{ minWidth: 260 }}
                              onChange={(e) =>
                                setMedios((prev) =>
                                  prev.map((x, idx) =>
                                    idx === i ? { ...x, caja: e.target.value } : x
                                  )
                                )
                              }
                            >
                              <option value="">-- Caja --</option>
                              {cajas.map((c) => (
                                <option key={c._id} value={c._id}>
                                  {c.descripcion} — Saldo:{" "}
                                  {Number(c.saldoActual || 0).toLocaleString("es-AR")}
                                </option>
                              ))}
                            </Form.Select>
                            {selCaja && (
                              <span className="text-muted">
                                Saldo: {Number(selCaja?.saldoActual || 0).toLocaleString("es-AR")}
                              </span>
                            )}
                          </div>
                        )}

                        {medio === "transferencia" && (
                          <div className="d-flex gap-2">
                            <Form.Select
                              size="sm"
                              value={m.cuentaBancaria || ""}
                              style={{ minWidth: 260 }}
                              onChange={(e) =>
                                setMedios((prev) =>
                                  prev.map((x, idx) =>
                                    idx === i ? { ...x, cuentaBancaria: e.target.value } : x
                                  )
                                )
                              }
                            >
                              <option value="">-- Cuenta --</option>
                              {cuentas.map((c) => (
                                <option key={c._id} value={c._id}>
                                  {(c.alias || c.numero || "Cuenta")} — Saldo:{" "}
                                  {Number(c.saldoActual || 0).toLocaleString("es-AR")}
                                </option>
                              ))}
                            </Form.Select>
                            {selCta && (
                              <span className="text-muted">
                                Saldo: {Number(selCta?.saldoActual || 0).toLocaleString("es-AR")}
                              </span>
                            )}
                          </div>
                        )}

                        {(medio === "cheque" || medio === "echeq") && (
                          <div className="d-flex gap-2">
                            <Form.Select
                              size="sm"
                              value={m.carteraItem || ""}
                              style={{ minWidth: 360 }}
                              onChange={(e) => {
                                const id = e.target.value;
                                const valor = carteraDisponibles.find(
                                  (v) => String(v._id) === String(id)
                                );

                                setMedios((prev) =>
                                  prev.map((x, idx) => {
                                    if (idx !== i) return x;

                                    if (!valor) {
                                      return {
                                        ...x,
                                        carteraItem: null,
                                        importe: 0,
                                        referencia: "",
                                        nroCheque: "",
                                        idICheq: "",
                                        bancoEmisor: "",
                                        emisor: "",
                                        librador: "",
                                        fechaCheque: "",
                                        fechaPagoDiferido: "",
                                        fechaLiquidacion: "",
                                      };
                                    }

                                    const esICheq = valor.tipo === "ICHEQ";

                                    return {
                                      ...x,
                                      medio: esICheq ? "echeq" : "cheque",
                                      carteraItem: valor._id,
                                      importe: Number(valor.importe || 0),

                                      referencia:
                                        valor.nro ||
                                        valor.nroCheque ||
                                        valor.idICheq ||
                                        "",

                                      nroCheque: valor.nroCheque || valor.nro || "",
                                      idICheq: valor.idICheq || valor.nro || "",

                                      bancoEmisor: valor.bancoEmisor || valor.banco || "",
                                      librador: valor.librador || valor.cobradoA || "",
                                      emisor: valor.emisor || valor.cobradoA || "",

                                      fechaCheque: valor.fechaCheque
                                        ? String(valor.fechaCheque).slice(0, 10)
                                        : "",
                                      fechaPagoDiferido: valor.fechaPagoDiferido
                                        ? String(valor.fechaPagoDiferido).slice(0, 10)
                                        : "",
                                      fechaLiquidacion: valor.fechaLiquidacion
                                        ? String(valor.fechaLiquidacion).slice(0, 10)
                                        : "",
                                    };
                                  })
                                );
                              }}
                            >
                              <option value="">-- Seleccionar valor de cartera --</option>

                              {carteraDisponibles
                                .filter((v) =>
                                  medio === "echeq" ? v.tipo === "ICHEQ" : v.tipo === "CHEQUE"
                                )
                                .map((v) => (
                                  <option key={v._id} value={v._id}>
                                    {labelValorCartera(v)}
                                  </option>
                                ))}
                            </Form.Select>

                            <Form.Control
                              size="sm"
                              value={m.referencia || m.nroCheque || m.idICheq || ""}
                              readOnly
                              style={{ minWidth: 150 }}
                              placeholder="N° / ID"
                            />

                            <Form.Control
                              size="sm"
                              value={m.bancoEmisor || ""}
                              readOnly
                              style={{ minWidth: 130 }}
                              placeholder="Banco"
                            />
                          </div>
                        )}
                      </td>

                      <td style={{ textAlign: "right", width: 160 }}>
                        <Form.Control
                          size="sm"
                          type="number"
                          step="0.01"
                          value={m.importe}
                          onChange={(e) =>
                            setMedios((prev) =>
                              prev.map((x, idx) =>
                                idx === i ? { ...x, importe: Number(e.target.value) } : x
                              )
                            )
                          }
                        />
                      </td>

                      <td style={{ width: 50 }}>
                        <Button variant="outline-danger" size="sm" onClick={() => removeMedio(i)}>
                          x
                        </Button>
                      </td>
                    </tr>
                  );
                })}

                {!medios.length && (
                  <tr>
                    <td colSpan={4} className="text-muted">
                      Agregá pagos si corresponde. También podés operar usando saldo a favor previo.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            {saldoWarnings.length > 0 && (
              <ul className="mt-2" style={{ color: "crimson" }}>
                {saldoWarnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            )}
          </Col>

          <Col md={6}>
            <h6>Retenciones</h6>
            <Button size="sm" onClick={() => addRet({ tipo: "IIBB", base: 0, porcentaje: 0, importe: 0 })}>
              + IIBB
            </Button>{" "}
            <Button size="sm" onClick={() => addRet({ tipo: "GAN", base: 0, porcentaje: 0, importe: 0 })}>
              + Ganancias
            </Button>{" "}
            <Button size="sm" onClick={() => addRet({ tipo: "IVA", base: 0, porcentaje: 0, importe: 0 })}>
              + IVA
            </Button>{" "}
            <Button size="sm" onClick={() => addRet({ tipo: "OTRA", base: 0, porcentaje: 0, importe: 0 })}>
              + Otra
            </Button>

            <Table size="sm" className="mt-2">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Base</th>
                  <th>%</th>
                  <th>Importe</th>
                  <th>Comp.</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rets.map((r, i) => (
                  <tr key={i}>
                    <td>
                      <Form.Select
                        size="sm"
                        value={r.tipo}
                        onChange={(e) =>
                          setRets((prev) =>
                            prev.map((x, idx) =>
                              idx === i ? { ...x, tipo: e.target.value } : x
                            )
                          )
                        }
                      >
                        <option value="IIBB">IIBB</option>
                        <option value="GAN">Ganancias</option>
                        <option value="IVA">IVA</option>
                        <option value="OTRA">OTRA</option>
                      </Form.Select>
                    </td>
                    <td style={{ width: 110 }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        value={r.base || 0}
                        onChange={(e) =>
                          setRets((prev) =>
                            prev.map((x, idx) =>
                              idx === i ? { ...x, base: Number(e.target.value) } : x
                            )
                          )
                        }
                      />
                    </td>
                    <td style={{ width: 90 }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        value={r.porcentaje || 0}
                        onChange={(e) =>
                          setRets((prev) =>
                            prev.map((x, idx) =>
                              idx === i ? { ...x, porcentaje: Number(e.target.value) } : x
                            )
                          )
                        }
                      />
                    </td>
                    <td style={{ width: 120 }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        value={r.importe || 0}
                        onChange={(e) =>
                          setRets((prev) =>
                            prev.map((x, idx) =>
                              idx === i ? { ...x, importe: Number(e.target.value) } : x
                            )
                          )
                        }
                      />
                    </td>
                    <td style={{ width: 140 }}>
                      <Form.Control
                        size="sm"
                        placeholder="Comprobante"
                        value={r.comprobante || ""}
                        onChange={(e) =>
                          setRets((prev) =>
                            prev.map((x, idx) =>
                              idx === i ? { ...x, comprobante: e.target.value } : x
                            )
                          )
                        }
                      />
                    </td>
                    <td>
                      <Button variant="outline-danger" size="sm" onClick={() => removeRet(i)}>
                        x
                      </Button>
                    </td>
                  </tr>
                ))}

                {!rets.length && (
                  <tr>
                    <td colSpan={6} className="text-muted">
                      Agregá retenciones si corresponde
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>

        <Row className="mt-2">
          <Col>
            <Form.Label>Observaciones</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
            />
          </Col>
        </Row>

        <Row className="mt-3">
          <Col md={4}>
            <b>Total aplicado a comandas:</b> ${$(totalAplic)}
          </Col>
          <Col md={4}>
            <b>Σ Pagos:</b> ${$(totalPagos)}
          </Col>
          <Col md={4}>
            <b>Σ Retenciones:</b> ${$(totalRets)}
          </Col>
        </Row>

        <Row>
          <Col md={4}>
            <b>Σ Saldo a favor usado:</b> ${$(totalSaldosFavorAplicados)}
          </Col>
          <Col md={4}>
            <b>Total disponible:</b> ${$(totalDisponible)}
          </Col>
          <Col md={4}>
            <b>Saldo a favor generado:</b> ${$(totalSaldoFavorGenerado)}
          </Col>
        </Row>

        <Row className="mt-2">
          <Col md={12}>
            <b>Diferencia disponible - aplicado:</b>{" "}
            <span
              style={{
                color: diferencia >= -0.01 ? "green" : "red",
                fontWeight: 700,
              }}
            >
              {$((diferencia))}
            </span>
          </Col>
        </Row>

        {!okDistribucion && (
          <Alert variant="danger" className="mt-3 mb-0">
            Lo aplicado a comandas supera el total disponible de la orden.
          </Alert>
        )}

        {okDistribucion && totalSaldoFavorGenerado > 0.01 && (
          <Alert variant="warning" className="mt-3 mb-0">
            Esta orden va a generar un saldo a favor de <b>${$(totalSaldoFavorGenerado)}</b>.
          </Alert>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={() => onHide(false)}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={guardar} disabled={!canSubmit}>
          Guardar y generar PDF
        </Button>
      </Modal.Footer>
    </Modal>
  );
}